import type { StrategyEvidenceKind } from "../mastery/strategy-mastery.ts";
import type { LessonActivity, StrategyLessonInstance } from "./lesson-engine.ts";

export interface LessonActivityResponse {
  readonly activityId: string;
  readonly answers: Readonly<Record<string, number>>;
  readonly helpRequested: boolean;
  readonly supportUsed: boolean;
}

export interface StrategyLessonResponse {
  readonly version: "lesson-response-v1";
  readonly lessonId: string;
  readonly activities: readonly LessonActivityResponse[];
}

export interface LessonAssessment {
  readonly evidenceKinds: readonly StrategyEvidenceKind[];
  readonly independentOutcome: "CORRECT" | "INCORRECT" | "HELP";
}

/** Derive evidence from the exact generated lesson; the UI cannot submit a mastery verdict. */
export function assessStrategyLesson(
  lesson: StrategyLessonInstance,
  response: StrategyLessonResponse,
): LessonAssessment {
  if (response.version !== "lesson-response-v1" || response.lessonId !== lesson.id) {
    throw new Error("Lesantwoord hoort niet bij deze lesversie.");
  }
  if (response.activities.length !== lesson.activities.length) {
    throw new Error("Lesantwoord mist activiteiten of bevat extra activiteiten.");
  }
  const responses = new Map(response.activities.map((item) => [item.activityId, item]));
  if (responses.size !== lesson.activities.length) throw new Error("Dubbel activiteitantwoord.");
  for (const activity of lesson.activities) {
    const item = responses.get(activity.id);
    if (!item) throw new Error(`Antwoord ontbreekt voor ${activity.id}.`);
    if (typeof item.helpRequested !== "boolean" || typeof item.supportUsed !== "boolean") {
      throw new Error("Lesantwoord mist expliciete hulpregistratie.");
    }
    const questionIds = new Set(activity.questions.map((question) => question.id));
    if (Object.entries(item.answers).some(([key, value]) => !questionIds.has(key) || !Number.isSafeInteger(value))) {
      throw new Error("Lesantwoord bevat onbekende vragen of ongeldige getallen.");
    }
    if (!item.helpRequested && Object.keys(item.answers).length !== questionIds.size) {
      throw new Error("Lesantwoord mist een antwoord zonder hulpverzoek.");
    }
  }
  const clean = (activity: LessonActivity): boolean => {
    const item = responses.get(activity.id)!;
    return !item.helpRequested && !item.supportUsed && activity.questions.length > 0
      && activity.questions.every((question) => item.answers[question.id] === question.expectedAnswer);
  };
  const independent = lesson.activities.find((activity) => activity.phase === "INDEPENDENT_CHECK")!;
  const independentResponse = responses.get(independent.id)!;
  const independentOutcome = independentResponse.helpRequested || independentResponse.supportUsed
    ? "HELP" : clean(independent) ? "CORRECT" : "INCORRECT";
  const evidenceKinds: StrategyEvidenceKind[] = [];
  if (lesson.variant === "FULL") evidenceKinds.push("INTRODUCTION_COMPLETED");
  else if (lesson.variant === "REFRESH") evidenceKinds.push("REFRESH_COMPLETED");
  const guided = lesson.activities.filter((activity) => activity.phase === "GUIDED_APPLICATION");
  if (guided.length > 0 && guided.every(clean)) evidenceKinds.push("GUIDED_SUCCESS");
  if (independentOutcome === "CORRECT") evidenceKinds.push("INDEPENDENT_SUCCESS");
  if (independentOutcome === "INCORRECT") evidenceKinds.push("INDEPENDENT_FAILURE");
  return { evidenceKinds, independentOutcome };
}
